import * as Sentry from '@sentry/browser';
import generateActions from 'utils/actions';
import { generateStyles } from 'utils/theme';
import { loadCurrentUser, setToken } from '../auth/actions';
import { setFilterI18nLabels } from '../charts/actions';
import Config from 'config';

export const SET_TITLE = 'app/SET_TITLE';
export const SET_MESSAGE = 'app/SET_MESSAGE';
export const SET_VALUE = 'app/SET_VALUE';
export const REMOVE_MESSAGE = 'app/REMOVE_MESSAGE';
export const GET_CONFIGURATIONS = generateActions('app/GET_CONFIGURATIONS');
export const GET_MAX_SIZE = generateActions('app/GET_MAX_SIZE');
export const POST_CONFIGURATIONS = generateActions('app/POST_CONFIGURATIONS');
export const POST_THEME = generateActions('app/POST_THEME');

/**
 * Bootstrap the APP!! All initial requirements
 * 
 * - Checks if there's a token in local storage and
 * then loads the user's information.
 */
export function bootstrap() {
  return (dispatch) => {
    const token = localStorage.getItem(Config.cookies.token);

    if (token) {
      // @TODO: create a common action we can use
      // for login and signup as well
      dispatch(setToken(token));
      dispatch(loadCurrentUser())
        .then(({ payload }) => {
          if (Config.sentry.dsn) {
            Sentry.configureScope(function (scope) {
              scope.setUser({
                id: payload.user.id,
                email: payload.user.email,
              });
            });
          }
        });
    }
  };
}

/**
 * Set the title to the document and adds
 * it to the reducer to be used in the header
 * @param {String} title 
 */
export function setTitle(title) {
  return {
    type: SET_TITLE,
    payload: {
      title,
    },
  };
}

/**
 * Updates all i18n values in redux
 */
export function updateI18nValues() {
  return dispatch => {
    dispatch(setFilterI18nLabels());
  };
}

/**
 * Adds a message to the queue
 * @param {Message} config 
 */
let counter = 1;
export function showMessage(config) {
  counter = counter + 1;
  return {
    type: SET_MESSAGE,
    payload: {
      config: {
        ...config,
        id: counter,
      },
    },
  };
}

/**
 * Removes a message from the queue
 * @param {Message} config 
 */
export function removeMessage(config) {
  return {
    type: REMOVE_MESSAGE,
    payload: {
      config,
    },
  };
}

/**
 * Load all configurations for the app
 */
export function loadConfigurations() {
  return {
    types: GET_CONFIGURATIONS,
    promise: {
      url: '/configurations',
    },
  };
}

/**
 * Load the max file size the server allows for uploads
 */
export function loadFileMaxSize() {
  return {
    types: GET_MAX_SIZE,
    promise: {
      url: '/configurations/file/size',
    },
  };
}

/**
 * Saves the given configurations
 * @param {Object} config 
 */
export function postConfigurations(config) {
  const form = {};
  Object.keys(config).forEach((key) => {
    if (key === 'APP_CUSTOM_THEME_STYLES') {
      form[key] = `${JSON.stringify(config[key])}`;
      if (config.APP_CURRENT_THEME === 'custom') {
        form.css = generateStyles(config.APP_CUSTOM_THEME_STYLES);
      }
    } else if (['APP_MENU', 'APP_FORUM_TOPICS'].includes(key)) {
      form[key] = `${JSON.stringify(config[key])}`;
    } else if (key === 'APP_LOGO') {
      // Only send logo when a new file is selected
      if (config[key] instanceof File) {
        form[key] = config[key];
      }
    } else {
      form[key] = config[key];
    }
  })
  return {
    types: POST_CONFIGURATIONS,
    promise: {
      url: '/configurations',
      method: 'POST',
      form,
    },
  };
}

/**
 * Apply the given theme to the app
 * @param {Object} theme 
 */
export function postTheme(theme) {
  return {
    types: POST_THEME,
    promise: {
      url: '/configurations/theme/apply',
      method: 'POST',
      data: {
        theme: theme.theme,
        css: generateStyles(theme),
      },
    },
  };
}

/**
 * Set the value for a given config property
 * @param {String} name 
 * @param {Any} value 
 */
export function setConfigValue(name, value) {
  return {
    type: SET_VALUE,
    payload: {
      name,
      value,
    }
  };
}
